import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { amount } from '../lib/format';
import '../styles/crm.css';

const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);

interface PubContact { name: string; position: string | null; phone: string | null; mobile: string | null; emails: string[]; isPrimary: boolean }
interface PubBank { bankName: string; holder: string | null; iban: string | null; accountNumber: string | null; swift: string | null; currency: string; isPrimary: boolean }
interface PubProfile {
  name: string; kindLabels: string[];
  country: string | null; address: string | null; city: string | null; postalCode: string | null;
  taxNumber: string | null; registrationNumber: string | null;
  email: string | null; phone: string | null; website: string | null;
  currency: string; paymentTermsDays: number; creditLimitMinor: number | null;
  kycLabel: string; riskLabel: string;
  contacts: PubContact[]; bankAccounts: PubBank[];
  issuer: { name: string; taxNumber: string | null };
}

function Block({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="cpv-label">{label}</div>
      <div className="cpv-value">{value || '—'}</div>
    </div>
  );
}

export function CounterpartyView() {
  const { token } = useParams<{ token: string }>();
  const [p, setP] = useState<PubProfile | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<PubProfile>(`/public/cp/${token}`).then(setP).catch((e) => setErr(e instanceof ApiError ? e.message : 'No se pudo cargar la ficha.'));
  }, [token]);

  const download = () => {
    if (PREVIEW) { window.print(); return; }
    window.open(`${window.location.origin}/api/public/cp/${token}/pdf`, '_blank');
  };

  if (err) {
    return (
      <div className="cpv-page">
        <div className="cpv-doc" style={{ textAlign: 'center', padding: 60 }}>
          <Icon name="alert" size={26} style={{ opacity: 0.4 }} />
          <h1 className="h2" style={{ margin: '12px 0 6px' }}>Ficha no disponible</h1>
          <p className="muted">{err}</p>
        </div>
      </div>
    );
  }
  if (!p) return <div className="cpv-page"><div className="faint" style={{ textAlign: 'center', paddingTop: 60 }}>Cargando ficha…</div></div>;

  const domicilio = [p.address, [p.postalCode, p.city].filter(Boolean).join(' '), p.country].filter(Boolean).join('\n');
  const generales = [p.email, p.phone, p.website].filter(Boolean).join('\n');

  return (
    <div className="cpv-page">
      <div className="cpv-doc">
        <div className="row between center">
          <span className="cpv-issuer">{p.issuer.name}</span>
          <span className="cpv-kicker">Ficha de contraparte</span>
        </div>
        <div className="cpv-rule" />

        <div className="cpv-name">{p.name}</div>
        <div className="cpv-class">
          {[p.kindLabels.join(' · '), p.country, `KYC: ${p.kycLabel}`, `Riesgo: ${p.riskLabel}`].filter(Boolean).join('     ')}
        </div>

        <div className="cpv-hair" />

        <div className="cpv-cols">
          <Block label="Domicilio" value={domicilio} />
          <Block label="Operating currency" value={p.currency} />
          <Block label="Tax ID" value={p.taxNumber ?? '—'} />
          <Block label="Payment term" value={`${p.paymentTermsDays} days`} />
          <Block label="Contacto general" value={generales} />
          <Block label="Credit limit" value={p.creditLimitMinor != null ? amount(p.creditLimitMinor, p.currency) : 'No limit'} />
        </div>

        <div className="cpv-hair" />

        <div className="cpv-sec">Personas de contacto</div>
        {p.contacts.length === 0 && <div className="cpv-value">Sin personas de contacto registradas.</div>}
        {p.contacts.map((c, i) => (
          <div key={i} className="cpv-item">
            <div className="cpv-item__name">{c.name}{c.isPrimary && <span className="cpv-item__tag">PRINCIPAL</span>}</div>
            {c.position && <div className="cpv-item__role">{c.position}</div>}
            {(c.phone || c.mobile) && (
              <div className="cpv-item__line">
                {[c.phone ? `Tel. ${c.phone}` : null, c.mobile ? `Mobile ${c.mobile}` : null].filter(Boolean).join('     ')}
              </div>
            )}
            {c.emails.length > 0 && <div className="cpv-item__line">{c.emails.join('     ')}</div>}
          </div>
        ))}

        <div className="cpv-hair" />

        <div className="cpv-sec">Cuentas bancarias</div>
        {p.bankAccounts.length === 0 && <div className="cpv-value">Sin cuentas bancarias registradas.</div>}
        {p.bankAccounts.map((b, i) => (
          <div key={i} className="cpv-item">
            <div className="cpv-bank__head">
              <span className="cpv-item__name">{b.bankName}</span>
              <span className="cpv-item__name">{b.currency}</span>
            </div>
            {b.holder && <div className="cpv-item__role">{b.holder}</div>}
            <div className="cpv-mono">{[b.iban ?? b.accountNumber, b.swift ? `SWIFT ${b.swift}` : null].filter(Boolean).join('     ') || '—'}</div>
            {b.isPrimary && <div className="cpv-item__tag" style={{ marginTop: 4 }}>CUENTA PRINCIPAL</div>}
          </div>
        ))}

        <div className="cpv-foot">
          <span>{p.issuer.name}{p.issuer.taxNumber ? `  ·  TRN ${p.issuer.taxNumber}` : ''}</span>
          <span>Documento interno · {new Date().toLocaleDateString('es-ES')}</span>
        </div>
      </div>

      <div className="cpv-actions cpv-noprint">
        <button className="btn btn--primary" onClick={download}><Icon name="download" size={16} /> Descargar PDF</button>
        <button className="btn btn--ghost" onClick={() => window.print()}><Icon name="file" size={16} /> Imprimir</button>
      </div>
    </div>
  );
}
